import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { cityPriceFaq, citySeo } from "../slugs/cityPricing";

const monaco: ChauffeurPage = {
  slug: "monaco",
  title: "Private chauffeur in Monaco – VTC",
  subtitle:
    "Private chauffeur in the Principality: Monte-Carlo, Port Hercule, the Grand Prix and the Nice airport link.",
  keywords: ["private chauffeur monaco", "vtc monaco", "monte-carlo chauffeur"],
  hero: {
    title: "Private chauffeur in Monaco",
    subtitle:
      "Monte-Carlo, the Casino, Port Hercule: discretion and punctuality in the Principality.",
    keywords: ["Monaco chauffeur", "VTC Monte-Carlo", "Principality chauffeur"],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur in Monaco",
    badges: ["Absolute discretion", "Nice airport link", "24/7 service"],
  },
  benefits: {
    heading: "Why a chauffeur in the Principality",
    items: [
      {
        title: "Discretion as standard",
        description:
          "Private clients, executives and residents: uniformed chauffeur, complete confidentiality.",
      },
      {
        title: "Nice airport link",
        description:
          "The Principality's classic run, by sedan or van, with flight tracking.",
      },
      {
        title: "Marquee events",
        description:
          "Monaco Grand Prix, Yacht Show, galas: proven logistics on tight schedules.",
      },
      {
        title: "Access mastery",
        description:
          "Casino, palaces and Port Hercule: drop-off as close as possible despite road closures.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Monaco",
    services: [
      {
        title: "Nice airport transfer",
        text: "Monaco ↔ Nice Côte d'Azur link, with heliport coordination available.",
      },
      {
        title: "Hourly hire",
        text: "A dedicated chauffeur by the hour or day for business meetings and evenings.",
      },
      {
        title: "Galas & nights out",
        text: "Casino de Monte-Carlo, the Opera, dinners and charity events, with a guaranteed late return.",
      },
      {
        title: "Grand Prix & Yacht Show",
        text: "Optimised travel around circuit closures and peak-crowd periods.",
      },
      {
        title: "Yacht & palace access",
        text: "Drop-off and pick-up at Port Hercule and outside the Principality's grand hotels.",
      },
    ],
  },
  locations: {
    heading: "Key points in the Principality",
    items: [
      {
        name: "Monte-Carlo",
        description: "Casino, Place du Casino, landmark hotels.",
      },
      {
        name: "Port Hercule",
        description: "Yachting quays and the main events zone.",
      },
      {
        name: "The Rock / Monaco-Ville",
        description: "Prince's Palace and old town.",
      },
      {
        name: "La Condamine",
        description: "Market, centre and shopping access.",
      },
      {
        name: "Fontvieille",
        description: "Residential quarter, port and heliport nearby.",
      },
      {
        name: "Larvotto",
        description: "Beaches and seafront residences.",
      },
    ],
  },
  generic: {
    heading: "Monaco: a dense territory where discretion comes first",
    subheading:
      "Sloping streets, tunnels and events: a chauffeur who knows the Principality.",
    paragraphs: [
      "Across 2 km², Monaco stacks tunnels, ramps and underground car parks. A chauffeur who masters its road network avoids detours, drops you closest to the Casino, a palace or a Port Hercule quay, and picks you back up without hesitation.",
      "The Principality's clientele — executives, residents, crews — expect discretion and punctuality above all. Uniformed chauffeur, immaculate car, nothing left to chance: the service makes itself forgotten.",
      "During the Grand Prix and the Monaco Yacht Show, whole streets close. We adapt the pick-up points and timing to keep your appointments despite the crowds.",
    ],
    bullets: [
      "Uniformed chauffeur",
      "Flight tracking",
      "Heliport coordination",
    ],
  },
  process: {
    heading: "Book a chauffeur in Monaco",
    steps: [
      "Give your address in the Principality (hotel, residence, quay, heliport).",
      "We set the route and time around the event and any closures.",
      "The chauffeur arrives in uniform, in an immaculate car, at the agreed time.",
      "Direct transfer or hourly hire to chain your appointments.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Monaco", "MONACO"),
    {
      q: "How long is a Monaco – Nice airport trip?",
      a: "About 35 to 45 minutes depending on traffic and the route (lower/middle corniche or A8). The chauffeur tracks your flight and adjusts the departure time.",
    },
    {
      q: "Can you coordinate a transfer with the Monaco heliport?",
      a: "Yes, we run the link to and from the Fontvieille heliport. Mention it at booking so we can align the timing with your flight.",
    },
    {
      q: "Do you operate during the Grand Prix or the Yacht Show?",
      a: "Yes. Many roads close during those periods; we adapt pick-up points and anticipate travel times to stay on schedule.",
    },
    {
      q: "Does the service guarantee discretion for a private clientele?",
      a: "Yes, that is our standard in the Principality: uniformed chauffeur, full confidentiality and drop-off as close as possible to palaces, residences or Port Hercule quays.",
    },
  ],
  relatedLinks: [
    {
      label: "Cannes to Monaco with a chauffeur, from 200 EUR",
      href: "/en/chauffeur/transfers/cannes-monaco",
    },
    {
      label: "Monaco Nice Airport shuttle",
      href: "/en/chauffeur/transfers/monaco-nice-airport",
    },
    {
      label: "Cannes Monaco shuttle",
      href: "/en/chauffeur/transfers/cannes-monaco",
    },
    {
      label: "Monaco Saint-Tropez shuttle",
      href: "/en/chauffeur/transfers/monaco-saint-tropez",
    },
  ],
  cta: {
    title: "Your private chauffeur in Monaco",
    text: "Airport, gala or Grand Prix: book a discreet chauffeur in the Principality.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    // MESURE 2026-10-09 : titre sans prix, page en position 36 à 45.
    ...citySeo("en", "Monaco", "MONACO", ", Monte-Carlo"),
    canonical: "/en/chauffeur/monaco",
  },
};

export default monaco;
